import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/apiError';
import { auditLogsApi } from './auditLogsApi';
import { parseChanges, type AuditLogDetail, type EntityChange } from './auditLogTypes';

export interface AuditLogEntryState {
  entry: AuditLogDetail | null;
  /** The parsed `changes` payload — one record per entity the request mutated. */
  changes: EntityChange[];
  loading: boolean;
  error: ApiError | null;
  /**
   * There is no entry to show, and that is a real answer rather than a fault — the
   * BY-INSTANT source only. Kept apart from `error` so a surface can say "nothing recorded"
   * without dressing it as something being broken.
   */
  missing: boolean;
  reload: () => void;
}

/**
 * The record and the instant its `Latest update` reports.
 *
 * The SECOND way to name one entry, for a reader who has a row in front of them and no entry
 * id at all. The resolution is the server's — see `auditLogsApi.entryAt`.
 */
export interface LatestUpdateSource {
  entityId: string;
  /** The record's `updatedAt`, verbatim off the wire. */
  at: string;
}

/**
 * Loads ONE audit entry, named either by its id or by the update it produced.
 *
 * Shared by the detail modal (§13), the profile page and the clickable `Latest update` cell,
 * so the fetch, the abort on unmount, the error normalization and the defensive `changes`
 * parse exist once. A second copy of this would be the place those surfaces started
 * disagreeing.
 *
 * **The two sources are exclusive and `id` wins**, which is not a tie-break anybody should
 * rely on: a caller passes one. Both absent requests nothing — the modal is closed, or the
 * route carried something that is not an entry id.
 */
export function useAuditLogEntry(
  id: number | null,
  latestUpdate?: LatestUpdateSource | null,
): AuditLogEntryState {
  /*
    The deps are PRIMITIVES, never the object: a `{ entityId, at }` literal is a new identity
    on every render, so the effect would re-run, call `setEntry`, re-render and loop until
    React stopped it — the failure `useDataTable`'s `fetchPage` is written against.
  */
  const entityId = latestUpdate?.entityId ?? null;
  const at = latestUpdate?.at ?? null;

  const [entry, setEntry] = useState<AuditLogDetail | null>(null);
  const [loading, setLoading] = useState(id !== null || (entityId !== null && at !== null));
  const [error, setError] = useState<ApiError | null>(null);
  const [missing, setMissing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (id === null && (entityId === null || at === null)) {
      setEntry(null);
      setError(null);
      setMissing(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setMissing(false);
    setEntry(null);

    const request =
      id !== null
        ? auditLogsApi.get(id, controller.signal)
        : auditLogsApi.entryAt(entityId as string, at as string, controller.signal);

    request
      .then(setEntry)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        const failure =
          caught instanceof ApiError ? caught : new ApiError('Could not load the entry.', 0);

        /*
          A 404 is not a failure for the BY-INSTANT source. The trail is append-only and
          nothing backfills it, so a record last written before the pipeline covered its
          module has no entry to find, and an error state would report that as broken.

          BY ID it keeps meaning what it always meant — the reference the reader was handed
          names no entry — which is the fault the profile page already reports, and why this
          does not simply route every 404 to `missing`.
        */
        if (failure.status === 404 && id === null) setMissing(true);
        else setError(failure);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id, entityId, at, reloadKey]);

  const changes = useMemo(() => parseChanges(entry?.changes ?? null), [entry]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { entry, changes, loading, error, missing, reload };
}
